<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import {
  THEME_LIGHT,
  THEME_TYPE_COLOR,
  THEME_TYPE_COLOR_GRADIENT,
  THEME_TYPE_PIC,
  THEME_TYPE_VIDEO,
  type ThemeDTO
} from '@/composables/memorize/memorize'
import { ElTooltip } from 'element-plus'

const props = defineProps({
  theme: {
    type: Object as PropType<ThemeDTO>,
    required: true
  },
  selectedId: {
    type: String,
    required: true
  },
  previewId: {
    type: String,
    required: false,
    default: ''
  }
})

const themeComputed = computed(() => props.theme)
const selectedIdRef = computed(() => props.selectedId)
const previewIdRef = computed(() => props.previewId)

function calGradientColor(colorValue: string | undefined) {
  if (colorValue) {
    const colors = colorValue.split('_')
    const startColor = colors[0]
    const endColor = colors[1]
    return {
      background: `linear-gradient(90deg, ${startColor}, ${endColor})`
    }
  } else {
    return { background: '#fff' }
  }
}

const calcImageBackground = computed(() => {
  if (themeComputed.value?.themeType == THEME_TYPE_PIC) {
    if (themeComputed.value?.smallFile || themeComputed.value?.file) {
      return {
        backgroundImage: `url(${
          themeComputed.value?.smallFile?.middleQuality || themeComputed.value?.file?.middleQuality
        })` /* 设置背景图片 */,
        backgroundSize: 'cover' /* 背景图片覆盖整个元素区域 */,
        backgroundPosition: 'center' /* 背景图片居中显示 */,
        backgroundRepeat: 'no-repeat' /* 背景图片不重复 */
      }
    } else {
      return { background: '#fff' }
    }
  } else {
    if (themeComputed.value?.gifSmallFile) {
      return {
        backgroundImage: `url(${
          themeComputed.value?.gifSmallFile?.middleQuality ||
          themeComputed.value?.file?.middleQuality ||
          themeComputed.value?.file?.url ||
          ''
        })` /* 设置背景图片 */,
        backgroundSize: 'cover' /* 背景图片覆盖整个元素区域 */,
        backgroundPosition: 'center' /* 背景图片居中显示 */,
        backgroundRepeat: 'no-repeat' /* 背景图片不重复 */
      }
    }
    return { background: '#fff' }
  }
})
</script>
<template>
  <div w-full flex="~ col" items-center>
    <div
      relative
      :class="{ 'border border-2 border-primary': themeComputed.id === previewIdRef }"
      p-0
      h-25
      w-full
      overflow-hidden
      rounded-1.5>
      <div
        v-if="themeComputed.id === selectedIdRef"
        opacity-90
        absolute
        top-1
        right-1
        bg-m-pressed
        text-inverted
        text-xs
        px-1
        py-0.4
        rounded-1>
        {{ $t('theme.current') }}
      </div>
      <div
        v-if="themeComputed?.memberOnly"
        absolute
        bottom-1
        px-1
        text-xs
        py-0.4
        left-1
        bg-user-pressed
        rounded-1
        text-inverted>
        {{ $t('alt.member_only') }}
      </div>
      <div
        v-if="themeComputed.themeType == THEME_TYPE_COLOR"
        h-full
        w-full
        :style="{ backgroundColor: themeComputed.colorValue || '#fff' }"></div>
      <div
        v-if="themeComputed.themeType == THEME_TYPE_COLOR_GRADIENT"
        h-full
        w-full
        :style="calGradientColor(themeComputed.colorValue)"></div>
      <div
        v-if="themeComputed.themeType == THEME_TYPE_PIC || themeComputed.themeType == THEME_TYPE_VIDEO"
        h-full
        w-full
        :style="calcImageBackground"></div>

      <el-tooltip
        v-if="themeComputed?.musicIds?.length > 0"
        :content="themeComputed?.playList?.map((item) => item?.name).join(',')"
        placement="top"
        effect="dark"
        :show-after="100">
        <div
          i-uil:music
          absolute
          p-2
          left-2
          top-2
          text-base
          opacity-80
          :class="themeComputed?.mode === THEME_LIGHT ? 'text-second' : 'text-inverted'"></div>
      </el-tooltip>
      <el-tooltip
        v-if="themeComputed.themeType == THEME_TYPE_VIDEO"
        :content="$t('m.dynamic_theme')"
        placement="top"
        effect="light"
        :show-after="500">
        <div absolute bottom-1 right-1 z-1 rounded p-1 class="bg-dark/[.4]">
          <div i-mingcute:wave-line text-inverted></div>
        </div>
      </el-tooltip>
    </div>
    <div my-1 text-inverted>{{ themeComputed.name }}</div>
  </div>
</template>
<style lang="scss" scoped></style>
